
import { UserProfile, BusinessRuleResult } from '../types';
import { adminService } from '@/core/services/AdminService';

export class PermissionRules {
  private static adminActions = [
    'view_admin_panel',
    'manage_users',
    'view_analytics',
    'manage_content',
    'access_logs'
  ];

  private static moderatorActions = [
    'moderate_content',
    'handle_reports',
    'view_user_details'
  ];

  static async hasPermission(user: UserProfile, action: string): Promise<BusinessRuleResult> {
    // Verificar se é admin via user_roles
    const isAdmin = await adminService.isAdmin(user.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador tem todas as permissões',
        metadata: { adminBypass: true }
      };
    }

    // Verificar permissões de admin
    if (this.adminActions.includes(action)) {
      return {
        allowed: isAdmin,
        reason: isAdmin
          ? 'Usuário tem permissão de administrador'
          : 'Permissão de administrador necessária'
      };
    }

    // Verificar permissões de moderador
    if (this.moderatorActions.includes(action)) {
      const isModerator = await adminService.isModerator(user.id);
      return {
        allowed: isModerator,
        reason: isModerator
          ? 'Usuário tem permissão de moderador'
          : 'Permissão de moderador necessária'
      };
    }

    // Ações gerais permitidas para todos
    return {
      allowed: true,
      reason: 'Ação permitida para todos os usuários'
    };
  }

  static async canAccessAdminPanel(user: UserProfile): Promise<BusinessRuleResult> {
    const isAdmin = await adminService.isAdmin(user.id);
    if (isAdmin) {
      return {
        allowed: true,
        reason: 'Administrador tem acesso completo',
        metadata: { role: 'admin' }
      };
    }
    
    const isModerator = await adminService.isModerator(user.id);
    if (isModerator) {
      return {
        allowed: true,
        reason: 'Moderador tem acesso restrito ao painel',
        metadata: { role: 'moderator' }
      };
    }
    
    return {
      allowed: false,
      reason: 'Acesso ao painel administrativo negado'
    };
  }

  static async canManageUsers(user: UserProfile): Promise<BusinessRuleResult> {
    return this.hasPermission(user, 'manage_users');
  }

  static async canAccessFeatureFlag(user: UserProfile, flagName: string): Promise<BusinessRuleResult> {
    // Verificar se é admin ou moderador para feature flags
    const isAdmin = await adminService.isAdmin(user.id);
    const isModerator = await adminService.isModerator(user.id);
    
    // Verificar se é usuário premium (qualquer plano que contenha 'premium' ou 'standard')
    const isPremium = user.plan_type?.includes('premium') || user.plan_type?.includes('standard');
    
    // Feature flags podem ter suas próprias regras baseadas em roles
    const featureFlags: Record<string, () => boolean> = {
      beta_features: () => isModerator || isAdmin,
      experimental_ui: () => isModerator || isAdmin || isPremium,
      debug_mode: () => isAdmin  // Apenas admins podem acessar debug_mode
    };

    const flagRule = featureFlags[flagName];
    
    if (!flagRule) {
      return {
        allowed: false,
        reason: `Feature flag '${flagName}' não encontrada`
      };
    }

    const hasAccess = flagRule();
    
    return {
      allowed: hasAccess,
      reason: hasAccess 
        ? `Usuário tem acesso ao feature flag '${flagName}'`
        : `Usuário não tem acesso ao feature flag '${flagName}'`
    };
  }
}
